/**
 * PWA install prompt utilities.
 *
 * IMPORTANT: getWebPlatform() detects the web browser/OS (iOS Safari, Android Chrome, desktop).
 * This is DIFFERENT from getPlatform()/isIos() in src/lib/platform.ts which detect Capacitor
 * native shells. On the web, those always return 'web'.
 */

// -- Types ------------------------------------------------------------------

export type WebPlatform = 'ios' | 'android' | 'desktop';
export type DesktopBrowser = 'safari' | 'firefox' | 'chromium';

// -- Type declaration for early-captured event (set in index.html) ----------

declare global {
  interface Window {
    __pwaInstallEvent: BeforeInstallPromptEvent | null;
  }
}

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

// -- Web platform detection -------------------------------------------------

export function getWebPlatform(): WebPlatform {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !('MSStream' in window)) return 'ios';
  if (ua.includes('Android')) return 'android';
  return 'desktop';
}

export function getDesktopBrowser(): DesktopBrowser {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'safari';
  return 'chromium';
}

/** Returns true if current browser supports PWA installation (excludes Firefox). */
export function canInstallPwa(): boolean {
  const platform = getWebPlatform();
  if (platform === 'ios') return true; // Manual Add to Home Screen
  if (platform === 'android') return true; // beforeinstallprompt
  // Desktop: Firefox has no PWA support
  return getDesktopBrowser() !== 'firefox';
}

// -- PWA installed detection ------------------------------------------------

export function isPwaInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as Record<string, unknown>).standalone === true)
  );
}

// -- Early-captured beforeinstallprompt event --------------------------------

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return window.__pwaInstallEvent ?? null;
}

export function clearDeferredInstallPrompt(): void {
  window.__pwaInstallEvent = null;
}
